import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useTabNavContext } from '../../tab-nav-context';

import Lazy from '@prisjakt/ui-lazy';

import {
  ProductTabMain,
  ProductTabProperties,
  ProductTabPropertiesExperiment,
  ProductTabReviews,
  ProductTabReviewsExperiment,
  ProductTabStatistics,
  ProductTabStatisticsExperiment,
} from './sections';
import AdContainer from 'src/components/ads';
import { PageSection } from 'src/components/page';
import ErrorBoundary from 'src/components/error-boundary';

import { useProductPageNavExperiment } from 'src/hooks/experiments';

import { delayedScrollIntoView } from './utils';

import { IProductPageTabsProps, TabAnchor } from './types';

import { AdWrapper, SectionWrapper } from './styles';

const SHOW_ALL = true;

export const ProductTabs: React.FC<IProductPageTabsProps> = props => {
  const { product, recommendations, currentCampaign } = props;

  const location = useLocation();
  const firstRender = useRef(true);

  const { activeTab, setActiveTab } = useTabNavContext();

  // Experiment on showing new tab navigation
  const [showNewTabNav, setShowNewTabNav] = useState(false);
  const productPageNavExperiment = useProductPageNavExperiment();

  useEffect(() => {
    setShowNewTabNav(productPageNavExperiment);
  }, [productPageNavExperiment]);

  useEffect(() => {
    // BASELINE: If hash in url, scroll into section view
    if (
      !productPageNavExperiment &&
      location.hash !== '' &&
      location.hash !== TabAnchor.ALL &&
      firstRender.current
    ) {
      firstRender.current = false;
      const targetElement = document.getElementById(location.hash)!;
      delayedScrollIntoView(targetElement);
    }

    // EXPERIMENT: set active tab by hash in url
    if (productPageNavExperiment && activeTab !== location.hash) {
      setActiveTab(location.hash.length === 0 ? TabAnchor.ALL : location.hash);
    }
  }, [location.hash, productPageNavExperiment]);

  if (!product) {
    return null;
  }

  const getIsVisible = (currentTab: string) => {
    if (!showNewTabNav || activeTab === TabAnchor.ALL) return SHOW_ALL;
    return activeTab === currentTab;
  };

  return (
    <>
      <SectionWrapper
        id={TabAnchor.ALL}
        $isVisible={getIsVisible(TabAnchor.ALL)}
        $newTabNav={showNewTabNav}
      >
        <ProductTabMain
          product={product}
          recommendations={recommendations}
          currentCampaign={currentCampaign}
        />
      </SectionWrapper>

      <SectionWrapper
        id={TabAnchor.REVIEWS}
        $isActive={activeTab === TabAnchor.REVIEWS}
        $isVisible={getIsVisible(TabAnchor.REVIEWS)}
        $newTabNav={showNewTabNav}
      >
        {showNewTabNav ? (
          <ProductTabReviewsExperiment product={product} />
        ) : (
          <ErrorBoundary onError={() => null}>
            <Lazy>
              <ProductTabReviews product={product} />
            </Lazy>
          </ErrorBoundary>
        )}
      </SectionWrapper>

      <SectionWrapper
        id={TabAnchor.STATISTICS}
        $isActive={activeTab === TabAnchor.STATISTICS}
        $isVisible={getIsVisible(TabAnchor.STATISTICS)}
        $newTabNav={showNewTabNav}
      >
        <PageSection
          noBorder
          noPad={
            showNewTabNav && activeTab !== TabAnchor.ALL ? 'both' : 'bottom'
          }
        >
          <AdWrapper $visible={!showNewTabNav || activeTab === TabAnchor.ALL}>
            <AdContainer
              placement="panorama_extra"
              format="panorama_1_wde"
              min={861}
              max={10000}
              scalable
              scaleBase=".pj-ui-panel--row"
              spacing={{
                top: '10px',
                bottom: '10px',
              }}
            />

            <AdContainer
              placement="dubbelpanorama1"
              format="panorama_1"
              min={0}
              max={860}
              spacing={{
                top: '10px',
                bottom: '10px',
              }}
            />
          </AdWrapper>

          {showNewTabNav ? (
            <ProductTabStatisticsExperiment product={product} />
          ) : (
            <ErrorBoundary onError={() => null}>
              <Lazy>
                <ProductTabStatistics product={product} />
              </Lazy>
            </ErrorBoundary>
          )}
        </PageSection>
      </SectionWrapper>

      <SectionWrapper
        id={TabAnchor.PROPERTIES}
        $isActive={activeTab === TabAnchor.PROPERTIES}
        $isVisible={getIsVisible(TabAnchor.PROPERTIES)}
        $newTabNav={showNewTabNav}
      >
        <PageSection noBorder noPad={showNewTabNav ? 'both' : undefined}>
          {showNewTabNav ? (
            <ProductTabPropertiesExperiment product={product} />
          ) : (
            <ErrorBoundary onError={() => null}>
              <Lazy>
                <ProductTabProperties product={product} />
              </Lazy>
            </ErrorBoundary>
          )}
        </PageSection>
      </SectionWrapper>
    </>
  );
};
